import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Route, useLocation } from "react-router-dom";
import { getPublicOrigin } from "utils/getPublicOrigin";

/** Upsert <meta name="..."> safely */
function upsertNamedMeta(name, content) {
  if (typeof document === "undefined") return;
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content || "");
}

/** Upsert <meta property="..."> safely (used for og:*) */
function upsertPropertyMeta(property, content) {
  if (typeof document === "undefined") return;
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content || "");
}

/** Replace any existing canonicals with a single correct one.
 *  If href is falsy, remove all canonicals and add nothing. */
function setCanonical(href) {
  if (typeof document === "undefined") return;
  [...document.querySelectorAll('link[rel="canonical"]')].forEach((el) => {
    if (el.parentNode) el.parentNode.removeChild(el);
  });
  if (!href) return; // explicit removal (e.g., 404)
  const link = document.createElement("link");
  link.setAttribute("rel", "canonical");
  link.setAttribute("href", href);
  document.head.appendChild(link);
}

/** Replace the route-level JSON-LD script tag.
 *  Uses its own id so it never collides with GlobalSchema's
 *  SiteNavigationElement block, which keeps rendering separately. */
function setRouteSchema(schema) {
  if (typeof document === "undefined") return;
  const existing = document.getElementById("route-schema");
  if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
  if (!schema) return;
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = "route-schema";
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
}

const RouteMiddleware = ({ path, component: Component, route, ...rest }) => {
  const location = useLocation();

  useEffect(() => {
    // GA4 page_view (guarded)
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: route?.title || document.title,
      });
    }

    if (typeof document === "undefined") return;

    // Origin used for every absolute URL below. getPublicOrigin() falls
    // back to the real production domain whenever the page is being
    // viewed from localhost (which is exactly what react-snap's internal
    // prerender server looks like), so frozen static output never bakes
    // in a broken localhost URL — see src/utils/getPublicOrigin.js.
    const origin = getPublicOrigin();
    const { pathname } =
      typeof window !== "undefined" && window.location
        ? window.location
        : { pathname: "" };

    // Title
    if (route?.title) document.title = route.title;

    // Route-defined meta tags (safe)
    (route?.meta || []).forEach((m) => {
      if (m?.name) upsertNamedMeta(m.name, m.content ?? "");
    });

    // Canonical + robots centralized here using route config
    const canonicalOpt = route?.canonical;
    const noindex = !!route?.noindex;

    // Canonical logic:
    // - canonical: "/path"   -> origin + "/path"
    // - canonical: null      -> remove canonical entirely (e.g., 404)
    // - canonical: undefined -> fallback to current URL
    if (canonicalOpt === null) {
      setCanonical(null); // remove any existing canonical
    } else if (typeof canonicalOpt === "string" && canonicalOpt.length) {
      setCanonical(`${origin}${canonicalOpt}`);
    } else {
      setCanonical(`${origin}${pathname}`);
    }

    // Robots logic based on route.noindex
    upsertNamedMeta("robots", noindex ? "noindex, nofollow" : "index, follow");

    // --- OG + Twitter, driven by route.og ---
    const descMeta = (route?.meta || []).find((m) => m?.name === "description");
    const fallbackDesc = descMeta?.content || "";
    const og = route?.og || {};

    const ogTitle = og.title || route?.title || "";
    const ogDesc = og.description || fallbackDesc;
    const ogImage = og.image ? `${origin}${og.image}` : `${origin}/1200wX630h.jpg`;
    const ogType = og.type || "website";
    const ogUrl =
      canonicalOpt === null
        ? `${origin}${pathname}`
        : typeof canonicalOpt === "string" && canonicalOpt.length
        ? `${origin}${canonicalOpt}`
        : `${origin}${pathname}`;

    upsertPropertyMeta("og:title", ogTitle);
    upsertPropertyMeta("og:description", ogDesc);
    upsertPropertyMeta("og:url", ogUrl);
    upsertPropertyMeta("og:type", ogType);
    upsertPropertyMeta("og:site_name", "Martley Strategy™");
    upsertPropertyMeta("og:image", ogImage);

    upsertNamedMeta("twitter:card", "summary_large_image");
    upsertNamedMeta("twitter:title", ogTitle);
    upsertNamedMeta("twitter:description", ogDesc);
    upsertNamedMeta("twitter:image", ogImage);

    // --- Route-specific JSON-LD, driven by route.schema ---
    // route.schema can be a plain object, OR a function(origin) => object
    // for routes that need dynamic absolute URLs (like BrandKeeper's Offers).
    const resolvedSchema =
      typeof route?.schema === "function" ? route.schema(origin) : route?.schema;
    setRouteSchema(resolvedSchema || null);
  }, [location, route]);

  return (
    <Route
      {...rest}
      path={path}
      render={(props) => <Component {...props} />}
    />
  );
};

RouteMiddleware.propTypes = {
  path: PropTypes.string,
  component: PropTypes.any.isRequired,
  route: PropTypes.object,
};

export default RouteMiddleware;